import React from 'react';
import { X, CheckCircle, AlertCircle, ChevronRight, Zap, Server, Battery, Shield, Snowflake } from 'lucide-react';
import {
  PowerGrid,
  LocationData,
  Transformer,
  Powerhouse,
  UPS
} from '../types';
import {
  TRANSFORMER_LEVELS,
  POWERHOUSE_LEVELS,
  UPS_LEVELS,
  GENERATOR_LEVELS
} from '../constants';
import { calculateLocationGrossPower } from '../utils/powerCalculations';

// We'll use the formatCurrency function passed as a prop instead of defining it here

// Segmented progress bar showing current level out of the max upgrade level.
const LevelTrack: React.FC<{ current: number; max: number }> = ({ current, max }) => (
  <div className="flex items-center gap-1 mt-1.5">
    {Array.from({ length: max }).map((_, i) => (
      <span key={i} className={`h-1.5 flex-1 rounded ${i < current ? 'bg-emerald-500' : 'bg-gray-600'}`} />
    ))}
    <span className="text-[11px] text-gray-400 ml-1 whitespace-nowrap">Lv {current}/{max}</span>
  </div>
);

// Preview of what the next upgrade delivers (current → next, per stat).
const NextLevelStats: React.FC<{ rows: { label: string; from: string; to: string }[] }> = ({ rows }) => (
  <div className="text-xs bg-gray-800/70 rounded px-2 py-1.5 mb-2">
    <div className="text-gray-400 mb-0.5">Next level:</div>
    {rows.map(r => (
      <div key={r.label} className="flex justify-between">
        <span className="text-gray-400">{r.label}</span>
        <span className="text-gray-300">{r.from} → <span className="text-emerald-300 font-medium">{r.to}</span></span>
      </div>
    ))}
  </div>
);

interface PowerManagementModalProps {
  showPowerManagementModal: boolean;
  setShowPowerManagementModal: (show: boolean) => void;
  currentLocationData: LocationData | null;
  currentLocation: string;
  money: number;
  buyPowerUpgrade: (locationId: string, upgradeType: 'transformer' | 'powerhouse' | 'ups', isNew: boolean) => void;
  selectPowerSupplier: (locationId: string, supplierId: string) => boolean;
  powerGrids: Record<string, PowerGrid>;
  triggerNotification: (message: string, type: 'success' | 'warning' | 'info' | 'error') => void;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  transformerLevels: typeof TRANSFORMER_LEVELS;
  powerhouseLevels: typeof POWERHOUSE_LEVELS;
  upsLevels: typeof UPS_LEVELS;
  buyGenerator: (locationId: string) => boolean;
  refuelGenerator: (locationId: string) => boolean;
  formatCurrency: (amount: number, decimals?: number) => string;
  currentHour?: number;
  onGoToCooling?: () => void; // closes this modal and opens the cooling chain (setup flow)
}

const PowerManagementModal: React.FC<PowerManagementModalProps> = ({
  showPowerManagementModal,
  setShowPowerManagementModal,
  currentLocationData,
  currentLocation,
  money,
  buyPowerUpgrade,
  selectPowerSupplier,
  powerGrids,
  triggerNotification,
  setLocations,
  setMoney,
  buyGenerator,
  refuelGenerator,
  transformerLevels,
  powerhouseLevels,
  upsLevels,
  formatCurrency,
  currentHour,
  onGoToCooling
}) => {
  // Power chain completeness — drives the "next: cooling" footer prompt.
  const powerChainComplete = !!currentLocationData?.selectedPowerSupplierId
    && !!currentLocationData?.transformer
    && !!currentLocationData?.powerhouse?.installed
    && !!currentLocationData?.ups?.installed;
  const resolveRateAtHour = (supplier: any, hour: number): number => {
    if (!supplier) return 0;
    const schedule = supplier.tariffSchedule as Array<{ startHour: number; endHour: number; rate: number }> | undefined;
    if (!schedule || typeof hour !== 'number') return supplier.costPerKWh;
    for (const win of schedule) {
      if (win.startHour <= win.endHour) {
        if (hour >= win.startHour && hour < win.endHour) return win.rate;
      } else {
        // Wrap-around window (e.g., 21 -> 7)
        if (hour >= win.startHour || hour < win.endHour) return win.rate;
      }
    }
    return supplier.costPerKWh;
  };

  const resolveCurrentRate = (supplier: any): number => {
    if (typeof currentHour !== 'number') return supplier.costPerKWh;
    return resolveRateAtHour(supplier, currentHour);
  };

  const hoursUntilNextChange = (supplier: any): number | null => {
    const schedule = supplier.tariffSchedule as Array<{ startHour: number; endHour: number; rate: number }> | undefined;
    if (!schedule || schedule.length === 0 || typeof currentHour !== 'number') return null;
    const currentRate = resolveRateAtHour(supplier, currentHour);
    for (let i = 1; i <= 24; i++) {
      const h = (currentHour + i) % 24;
      const r = resolveRateAtHour(supplier, h);
      if (r !== currentRate) return i;
    }
    return null;
  };

  const formatSchedule = (supplier: any): string | null => {
    const schedule = supplier.tariffSchedule as Array<{ startHour: number; endHour: number; rate: number }> | undefined;
    if (!schedule || schedule.length === 0) return null;
    const rates = schedule.map(w => w.rate);
    const minR = Math.min(...rates);
    const maxR = Math.max(...rates);
    const labelFor = (r: number) => (r === minR ? 'Off-peak' : r === maxR ? 'Peak' : 'Mid');
    return schedule
      .map(w => `${w.startHour.toString().padStart(2, '0')}-${w.endHour.toString().padStart(2, '0')}: ${formatCurrency(w.rate, 3)} (${labelFor(w.rate)})`)
      .join('  |  ');
  };

  const classifyPhase = (supplier: any): 'Peak' | 'Off-peak' | 'Mid' | 'Fixed' => {
    const schedule = supplier.tariffSchedule as Array<{ startHour: number; endHour: number; rate: number }> | undefined;
    if (!schedule || schedule.length === 0 || typeof currentHour !== 'number') return 'Fixed';
    const rates = schedule.map(w => w.rate);
    const minR = Math.min(...rates);
    const maxR = Math.max(...rates);
    const cr = resolveCurrentRate(supplier);
    const tol = 1e-9;
    if (Math.abs(cr - minR) < tol) return 'Off-peak';
    if (Math.abs(cr - maxR) < tol) return 'Peak';
    return 'Mid';
  };

  // Recommend a grid per location — every location maps to its country grid
  const recommendGridForLocation = (locKey: string, grids: Record<string, PowerGrid>): string | null => {
    const prefs: Record<string, string> = {
      rotterdam:  'netherlands-grid',
      suburban:   'netherlands-grid',
      amsterdam:  'netherlands-grid',
      industrial: 'germany-grid',
      london:     'uk-grid',
      paris:      'france-grid',
      madrid:     'spain-grid',
      milan:      'italy-grid',
      stockholm:  'sweden-grid',
      warsaw:     'poland-grid',
      bucharest:  'romania-grid',
      istanbul:   'turkey-grid',
      losangeles:   'us-grid',
      seattle:      'us-grid',
      dallas:       'us-grid',
      chicago:      'us-grid',
      ashburn:      'us-grid',
      newark:       'us-grid',
      saopaulo:     'brazil-grid',
      santiago:     'chile-grid',
      buenosaires:  'argentina-grid',
      johannesburg: 'southafrica-grid',
      dubai:        'uae-grid',
      hongkong:     'hongkong-grid',
      tokyo:        'japan-grid',
      sydney:       'australia-grid',
    };
    const preferred = prefs[locKey];
    if (preferred && grids[preferred]) return preferred;
    const first = Object.keys(grids)[0];
    return first || null;
  };

  const recommendedGridId = recommendGridForLocation(currentLocation, powerGrids);
  const [selectedGridId, setSelectedGridId] = React.useState<string | null>(recommendedGridId);

  React.useEffect(() => {
    // Sync selector with current location and connection state
    if (!currentLocationData?.powerGridId) {
      setSelectedGridId(recommendGridForLocation(currentLocation, powerGrids));
    } else {
      setSelectedGridId(currentLocationData.powerGridId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, showPowerManagementModal, powerGrids, currentLocationData?.powerGridId]);

  // Guard after hooks setup to avoid conditional hook calls
  if (!showPowerManagementModal || !currentLocationData) {
    return null;
  }

  // Derive chain step states (null-safe: currentLocationData may be null before guard)
  const gridOk = !!currentLocationData?.powerGridId;
  const supplierOk = !!currentLocationData?.selectedPowerSupplierId;
  const transformerOk = !!(currentLocationData?.transformer?.connected && currentLocationData?.transformer?.capacityKW);
  const powerhouseOk = !!(currentLocationData?.powerhouse?.installed && currentLocationData?.powerhouse?.capacityKW);
  const upsOk = !!(currentLocationData?.ups?.installed && currentLocationData?.ups?.maxOutputKW);
  const racksReady = gridOk && supplierOk && transformerOk && powerhouseOk && upsOk;

  const usedKW = currentLocationData?.usedPower ?? 0;
  const availKW = currentLocationData?.availablePowerKW ?? 0;
  const grossKW = currentLocationData?.grossPowerKW ?? availKW;
  const powerPct = grossKW > 0 ? Math.min(100, (usedKW / grossKW) * 100) : 0;
  const powerBarColor = powerPct > 90 ? 'bg-red-500' : powerPct > 70 ? 'bg-amber-500' : 'bg-emerald-500';

  type StepStatus = 'ok' | 'warning' | 'missing';
  const chainSteps: { label: string; sub: string; status: StepStatus; icon: React.ReactNode }[] = [
    {
      label: 'Grid',
      sub: gridOk ? (powerGrids[currentLocationData.powerGridId!]?.locationName?.replace(' Power Grid', '') ?? 'Connected') : 'Not connected',
      status: gridOk ? 'ok' : 'missing',
      icon: <Zap className="w-5 h-5" />,
    },
    {
      label: 'Supplier',
      sub: supplierOk
        ? (() => {
            const grid = currentLocationData.powerGridId ? powerGrids[currentLocationData.powerGridId] : null;
            const sup = grid?.suppliers.find(s => s.id === currentLocationData.selectedPowerSupplierId);
            return sup?.name ?? 'Selected';
          })()
        : 'None selected',
      status: !gridOk ? 'missing' : supplierOk ? 'ok' : 'warning',
      icon: <Zap className="w-5 h-5" />,
    },
    {
      label: 'Transformer',
      sub: transformerOk ? `L${currentLocationData.transformer!.level} · ${currentLocationData.transformer!.capacityKW} kW` : 'Not installed',
      status: !supplierOk ? 'missing' : transformerOk ? 'ok' : 'warning',
      icon: <Server className="w-5 h-5" />,
    },
    {
      label: 'Distribution',
      sub: powerhouseOk ? `L${currentLocationData.powerhouse!.level} · ${currentLocationData.powerhouse!.capacityKW} kW` : 'Not installed',
      status: !transformerOk ? 'missing' : powerhouseOk ? 'ok' : 'warning',
      icon: <Server className="w-5 h-5" />,
    },
    {
      label: 'UPS',
      sub: upsOk
        ? `L${currentLocationData.ups!.level} · ${currentLocationData.ups!.capacityKWh} kWh${currentLocationData.ups!.discharging ? ' · DISCHARGING' : currentLocationData.ups!.charging ? ' · charging' : ''}`
        : 'Not installed',
      status: !powerhouseOk ? 'missing' : upsOk ? (currentLocationData.ups?.discharging ? 'warning' : 'ok') : 'warning',
      icon: <Battery className="w-5 h-5" />,
    },
    {
      label: 'Racks',
      sub: racksReady ? `${usedKW.toFixed(1)} / ${grossKW.toFixed(1)} kW` : 'Awaiting chain',
      status: racksReady ? (powerPct > 90 ? 'warning' : 'ok') : 'missing',
      icon: <Shield className="w-5 h-5" />,
    },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Power Management</h2>
          <button
            onClick={() => setShowPowerManagementModal(false)}
            className="text-gray-400 hover:text-white text-2xl"
          >
            <X />
          </button>
        </div>

        {/* ── Power Chain Flow Diagram ─────────────────────────────────── */}
        <div className="mb-6 bg-gray-900/60 rounded-xl p-4 border border-gray-700">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Power delivery chain</p>
          <div className="flex items-center gap-1 flex-wrap">
            {chainSteps.map((step, i) => {
              const okCls   = 'bg-emerald-900/40 border-emerald-600 text-emerald-300';
              const warnCls = 'bg-amber-900/40 border-amber-500 text-amber-300';
              const missCls = 'bg-gray-700/40 border-gray-600 text-gray-500';
              const cls = step.status === 'ok' ? okCls : step.status === 'warning' ? warnCls : missCls;
              return (
                <React.Fragment key={step.label}>
                  <div className={`flex flex-col items-center px-3 py-2 rounded-lg border ${cls} min-w-[80px]`}>
                    <div className="flex items-center gap-1 mb-1">
                      {step.status === 'ok'
                        ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                        : step.status === 'warning'
                        ? <AlertCircle className="w-4 h-4 text-amber-400" />
                        : <AlertCircle className="w-4 h-4 text-gray-500" />}
                      <span className="text-xs font-bold">{step.label}</span>
                    </div>
                    <span className="text-[10px] text-center leading-tight opacity-80">{step.sub}</span>
                  </div>
                  {i < chainSteps.length - 1 && (
                    <ChevronRight className={`w-4 h-4 shrink-0 ${step.status === 'ok' ? 'text-emerald-600' : 'text-gray-600'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {/* Power utilisation bar */}
          {racksReady && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Power utilisation</span>
                <span>{usedKW.toFixed(1)} / {grossKW.toFixed(1)} kW ({powerPct.toFixed(0)}%)</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className={`${powerBarColor} h-2 rounded-full transition-all duration-500`} style={{ width: `${powerPct}%` }} />
              </div>
            </div>
          )}
        </div>

        {currentLocationData && currentLocationData.powerGridId && (
          <div className="space-y-6">
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-3">Power Grid Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400 mb-2">Connected to: {powerGrids[currentLocationData.powerGridId]?.locationName || 'Unknown Grid'}</p>
                  <p className="text-sm text-gray-400 mb-2">Total Capacity: {powerGrids[currentLocationData.powerGridId]?.totalCapacityGW.toFixed(1) || '0'} GW</p>
                  <p className="text-sm text-gray-400 mb-2">Current Load: {powerGrids[currentLocationData.powerGridId]?.currentLoadGW.toFixed(1) || '0'} GW</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-2">Power: {currentLocationData.usedPower?.toFixed(1) || '0'} / {currentLocationData.grossPowerKW?.toFixed(1) || currentLocationData.availablePowerKW?.toFixed(1)} kW</p>
                  <p className="text-sm text-gray-400 mb-2">Cooling Capacity: {powerGrids[currentLocationData.powerGridId]?.coolingCapacity?.toFixed(1) || '0'} kW</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-3">Transformer</h3>
              {currentLocationData.transformer ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold">Level {currentLocationData.transformer.level} Transformer</p>
                      <p className="text-xs text-gray-400">Capacity: {currentLocationData.transformer.capacityKW} kW</p>
                      <p className="text-xs text-gray-400">Efficiency: {(currentLocationData.transformer.efficiency * 100).toFixed(1)}%</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`w-3 h-3 rounded-full ${currentLocationData.transformer.connected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <span className="text-xs">{currentLocationData.transformer.connected ? 'Connected' : 'Disconnected'}</span>
                    </div>
                  </div>
                  <LevelTrack current={currentLocationData.transformer.level} max={transformerLevels.length - 1} />
                  {(() => {
                    const cur = currentLocationData.transformer!;
                    const next = transformerLevels[cur.level + 1];
                    if (!next) return <p className="text-xs text-emerald-400">Maximum level reached — fully upgraded.</p>;
                    return (
                      <>
                        <NextLevelStats rows={[
                          { label: 'Capacity', from: `${cur.capacityKW} kW`, to: `${next.capacityKW} kW (+${next.capacityKW - cur.capacityKW})` },
                          { label: 'Efficiency', from: `${(cur.efficiency * 100).toFixed(1)}%`, to: `${(next.efficiency * 100).toFixed(1)}%` },
                        ]} />
                        <button
                          className="w-full py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                          onClick={() => buyPowerUpgrade(currentLocation, 'transformer', false)}
                          disabled={money < next.cost}
                        >
                          Upgrade to Level {next.level} - {formatCurrency(next.cost)}
                        </button>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400 mb-1">No transformer installed</p>
                  <p className="text-xs text-gray-500 mb-3">{transformerLevels.length - 1} levels · L1: {transformerLevels[1].capacityKW} kW @ {(transformerLevels[1].efficiency * 100).toFixed(1)}% → max L{transformerLevels.length - 1}: {transformerLevels[transformerLevels.length - 1].capacityKW} kW</p>
                  <button
                    className="py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                    onClick={() => buyPowerUpgrade(currentLocation, 'transformer', true)}
                    disabled={money < transformerLevels[1].cost}
                    title={money < transformerLevels[1].cost ? `Need ${formatCurrency(transformerLevels[1].cost - money)} more` : 'Install transformer'}
                  >
                    Install Transformer (L1) - ${formatCurrency(transformerLevels[1].cost)}
                  </button>
                </div>
              )}
            </div>
            
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-3">Power Distribution (MDB)</h3>
              {currentLocationData.powerhouse ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold">Level {currentLocationData.powerhouse.level} Distribution</p>
                    <p className="text-xs text-gray-400">Capacity: {currentLocationData.powerhouse.capacityKW} kW</p>
                    <p className="text-xs text-gray-400">Efficiency: {(currentLocationData.powerhouse.efficiency * 100).toFixed(1)}%</p>
                  </div>
                  <LevelTrack current={currentLocationData.powerhouse.level} max={powerhouseLevels.length - 1} />
                  {(() => {
                    const cur = currentLocationData.powerhouse!;
                    const next = powerhouseLevels[cur.level + 1];
                    if (!next) return <p className="text-xs text-emerald-400">Maximum level reached — fully upgraded.</p>;
                    return (
                      <>
                        <NextLevelStats rows={[
                          { label: 'Capacity', from: `${cur.capacityKW} kW`, to: `${next.capacityKW} kW (+${next.capacityKW - cur.capacityKW})` },
                          { label: 'Efficiency', from: `${(cur.efficiency * 100).toFixed(1)}%`, to: `${(next.efficiency * 100).toFixed(1)}%` },
                        ]} />
                        <button
                          className="w-full py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                          onClick={() => buyPowerUpgrade(currentLocation, 'powerhouse', false)}
                          disabled={money < next.cost}
                        >
                          Upgrade to Level {next.level} - {formatCurrency(next.cost)}
                        </button>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400 mb-1">No power distribution installed</p>
                  <p className="text-xs text-gray-500 mb-3">{powerhouseLevels.length - 1} levels · L1: {powerhouseLevels[1].capacityKW} kW @ {(powerhouseLevels[1].efficiency * 100).toFixed(1)}% → max L{powerhouseLevels.length - 1}: {powerhouseLevels[powerhouseLevels.length - 1].capacityKW} kW</p>
                  <button
                    className="py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                    onClick={() => buyPowerUpgrade(currentLocation, 'powerhouse', true)}
                    disabled={!currentLocationData.transformer || money < powerhouseLevels[1].cost}
                    title={!currentLocationData.transformer ? 'Install a Transformer first' : money < powerhouseLevels[1].cost ? `Need ${formatCurrency(powerhouseLevels[1].cost - money)} more` : 'Install power distribution'}
                  >
                    Install Distribution (L1) - ${formatCurrency(powerhouseLevels[1].cost)}
                  </button>
                </div>
              )}
            </div>
            
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-3">UPS System</h3>
              {currentLocationData.ups ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-semibold">Level {currentLocationData.ups.level} UPS</p>
                      <p className="text-xs text-gray-400">Capacity: {currentLocationData.ups.capacityKWh} kWh</p>
                      <p className="text-xs text-gray-400">Max Output: {currentLocationData.ups.maxOutputKW} kW</p>
                      <p className="text-xs text-gray-400">Backup Time: {currentLocationData.ups.backupTimeMinutes} minutes</p>
                    </div>
                    <div className="text-right">
                      {currentLocationData.ups.discharging ? (
                        <span className="px-2 py-1 bg-red-700 text-red-200 text-xs rounded font-semibold animate-pulse">DISCHARGING</span>
                      ) : currentLocationData.ups.charging ? (
                        <span className="px-2 py-1 bg-blue-700 text-blue-200 text-xs rounded font-semibold">CHARGING</span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-600 text-gray-300 text-xs rounded">STANDBY</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Battery Charge</span>
                      <span>{currentLocationData.ups.chargeLevelKWh.toFixed(1)} / {currentLocationData.ups.capacityKWh} kWh
                        ({((currentLocationData.ups.chargeLevelKWh / currentLocationData.ups.capacityKWh) * 100).toFixed(0)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-3">
                      {(() => {
                        const pct = (currentLocationData.ups.chargeLevelKWh / currentLocationData.ups.capacityKWh) * 100;
                        const color = currentLocationData.ups.discharging ? 'bg-red-500' : pct > 50 ? 'bg-green-500' : pct > 20 ? 'bg-yellow-500' : 'bg-red-500';
                        return <div className={`${color} h-3 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />;
                      })()}
                    </div>
                  </div>
                  <LevelTrack current={currentLocationData.ups.level} max={upsLevels.length - 1} />
                  {(() => {
                    const cur = currentLocationData.ups!;
                    const next = upsLevels[cur.level + 1];
                    if (!next) return <p className="text-xs text-emerald-400">Maximum level reached — fully upgraded.</p>;
                    return (
                      <>
                        <NextLevelStats rows={[
                          { label: 'Capacity', from: `${cur.capacityKWh} kWh`, to: `${next.capacityKWh} kWh (+${next.capacityKWh - cur.capacityKWh})` },
                          { label: 'Max output', from: `${cur.maxOutputKW} kW`, to: `${next.maxOutputKW} kW (+${next.maxOutputKW - cur.maxOutputKW})` },
                          { label: 'Efficiency', from: `${(cur.efficiency * 100).toFixed(1)}%`, to: `${(next.efficiency * 100).toFixed(1)}%` },
                        ]} />
                        <button
                          className="w-full py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                          onClick={() => buyPowerUpgrade(currentLocation, 'ups', false)}
                          disabled={money < next.cost}
                        >
                          Upgrade to Level {next.level} - {formatCurrency(next.cost)}
                        </button>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400 mb-1">No UPS installed</p>
                  <p className="text-xs text-gray-500 mb-3">{upsLevels.length - 1} levels · L1: {upsLevels[1].maxOutputKW} kW / {upsLevels[1].capacityKWh} kWh → max L{upsLevels.length - 1}: {upsLevels[upsLevels.length - 1].maxOutputKW} kW / {upsLevels[upsLevels.length - 1].capacityKWh} kWh</p>
                  <button
                    className="py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                    onClick={() => buyPowerUpgrade(currentLocation, 'ups', true)}
                    disabled={!currentLocationData.powerhouse?.installed || money < upsLevels[1].cost}
                    title={!currentLocationData.powerhouse?.installed ? 'Install Power Distribution first' : money < upsLevels[1].cost ? `Need ${formatCurrency(upsLevels[1].cost - money)} more` : 'Install UPS'}
                  >
                    Install UPS (L1) - ${formatCurrency(upsLevels[1].cost)}
                  </button>
                </div>
              )}
            </div>
            {/* Diesel Generator */}
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-3">Diesel Generator</h3>
              {currentLocationData.generator?.installed ? (() => {
                const gen = currentLocationData.generator!;
                const nextLevel = gen.level + 1;
                const nextSpec = nextLevel < GENERATOR_LEVELS.length ? GENERATOR_LEVELS[nextLevel] : null;
                const fuelPct = gen.runtimeHours > 0 ? (gen.fuelLevelHours / gen.runtimeHours) * 100 : 0;
                const fuelColor = fuelPct > 50 ? 'bg-green-500' : fuelPct > 20 ? 'bg-yellow-500' : 'bg-red-500';
                const missingHours = gen.runtimeHours - gen.fuelLevelHours;
                const refuelCost = Math.ceil(missingHours * 15);
                return (
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold">Level {gen.level} Generator</p>
                        <p className="text-xs text-gray-400">Output: {gen.powerOutputKW} kW · Tank: {gen.runtimeHours}h · Fuel cost: {formatCurrency(gen.fuelCostPerHour)}/h</p>
                      </div>
                      <div>
                        {gen.isRunning ? (
                          <span className="px-2 py-1 bg-amber-700 text-amber-200 text-xs rounded font-semibold animate-pulse">RUNNING</span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-600 text-gray-300 text-xs rounded">STANDBY</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Fuel level</span>
                        <span>{gen.fuelLevelHours.toFixed(1)}h / {gen.runtimeHours}h ({fuelPct.toFixed(0)}%)</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-3">
                        <div className={`${fuelColor} h-3 rounded-full transition-all duration-500`} style={{ width: `${fuelPct}%` }} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="flex-1 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                        onClick={() => refuelGenerator(currentLocation)}
                        disabled={missingHours <= 0.01 || money < refuelCost}
                      >
                        {missingHours <= 0.01 ? 'Tank full' : `Refuel — ${formatCurrency(refuelCost)}`}
                      </button>
                      {nextSpec && (
                        <button
                          className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                          onClick={() => buyGenerator(currentLocation)}
                          disabled={money < nextSpec.cost}
                        >
                          Upgrade to L{nextLevel} — {formatCurrency(nextSpec.cost)}
                        </button>
                      )}
                      {!nextSpec && (
                        <button className="flex-1 py-2 bg-gray-600 rounded text-sm font-semibold cursor-default" disabled>Max Level</button>
                      )}
                    </div>
                  </div>
                );
              })() : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400 mb-1">No generator installed.</p>
                  <p className="text-xs text-gray-500 mb-3">A diesel generator kicks in automatically during power outages, keeping your servers online while the UPS handles momentary gaps.</p>
                  <button
                    className="py-2 px-4 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold"
                    onClick={() => buyGenerator(currentLocation)}
                    disabled={money < GENERATOR_LEVELS[1].cost}
                  >
                    Install Generator (L1, {GENERATOR_LEVELS[1].runtimeHours}h tank) — {formatCurrency(GENERATOR_LEVELS[1].cost)}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Power Grid Selection UI */}
        <div className="mt-6 bg-gray-800 p-4 rounded-lg">
          <h3 className="font-semibold text-lg mb-3">Power Grid Connection</h3>
          {currentLocationData?.powerGridId ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold">{powerGrids[currentLocationData.powerGridId]?.locationName || 'Connected to Grid'}</p>
                  <p className="text-xs text-gray-400">Total Capacity: {powerGrids[currentLocationData.powerGridId]?.totalCapacityGW || 0} GW</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-xs">Connected</span>
                  <button
                    className="ml-3 px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-xs font-semibold"
                    onClick={() => {
                      const confirmed = window.confirm('Disconnect from the current power grid? This will cut power until you reconnect.');
                      if (!confirmed) return;
                      setLocations(prev => {
                        const loc = prev[currentLocation];
                        if (!loc) return prev;
                        const updated = JSON.parse(JSON.stringify(loc));
                        updated.powerGridId = null;
                        updated.selectedPowerSupplierId = null;
                        updated.grossPowerKW = 0;
                        updated.availablePowerKW = 0;
                        const next = { ...prev };
                        next[currentLocation] = updated;
                        return next;
                      });
                      triggerNotification('Disconnected from power grid.', 'warning');
                    }}
                  >
                    Disconnect
                  </button>
                </div>
              </div>
              <div className="mt-4">
                {(() => {
                  const hasSupplier = !!currentLocationData.selectedPowerSupplierId;
                  const transformerOk = !!(currentLocationData.transformer?.connected && currentLocationData.transformer?.capacityKW);
                  const powerhouseOk = !!(currentLocationData.powerhouse?.installed && currentLocationData.powerhouse?.capacityKW);
                  const upsInstalled = !!(currentLocationData.ups?.installed && currentLocationData.ups?.maxOutputKW);
                  if (!hasSupplier || !transformerOk || !powerhouseOk || !upsInstalled) {
                    return (
                      <div className="mb-3 p-2 rounded bg-amber-900/30 border border-amber-700/40 text-amber-200 text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                          Power delivery requirements
                        </div>
                        <ul className="list-disc pl-5 space-y-1">
                          {!hasSupplier && <li>Select a power supplier.</li>}
                          {!transformerOk && <li>Install and connect a transformer with capacity.</li>}
                          {!powerhouseOk && <li>Install power distribution (MDB) with capacity.</li>}
                          {!upsInstalled && <li>Install a UPS with capacity.</li>}
                        </ul>
                      </div>
                    );
                  }
                  return null;
                })()}
                <h4 className="text-sm font-semibold mb-2">Available Power Suppliers</h4>
                {!currentLocationData.selectedPowerSupplierId && (
                  <div className="mb-2 flex items-center gap-2 text-amber-300 text-xs">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                    No supplier selected. Select a supplier below to receive power.
                  </div>
                )}
                <div className="space-y-2">
                  {(() => {
                    const suppliers = powerGrids[currentLocationData.powerGridId]?.suppliers || [];
                    const data = suppliers
                      .map(s => ({ s, currentRate: resolveCurrentRate(s), nextChangeH: hoursUntilNextChange(s) }))
                      .sort((a, b) => a.currentRate - b.currentRate);
                    const minCurrentRate = data.length ? data[0].currentRate : Infinity;
                    return data.map(({ s: supplier, currentRate, nextChangeH }) => {
                      const scheduleText = formatSchedule(supplier);
                      const phase = classifyPhase(supplier);
                      const isBestNow = currentRate === minCurrentRate;
                      const isSelected = currentLocationData.selectedPowerSupplierId === supplier.id;
                      const badgeClass = phase === 'Peak' ? 'bg-red-600/80 text-white' : phase === 'Off-peak' ? 'bg-emerald-600/80 text-white' : phase === 'Mid' ? 'bg-amber-600/80 text-white' : 'bg-slate-600/80 text-white';
                      const containerClasses = `flex justify-between items-center p-2 rounded ${isSelected ? 'ring-2 ring-blue-500 bg-blue-900/10' : 'ring-0 hover:ring-1 hover:ring-gray-600'} bg-gray-700 transition`;
                      return (
                        <div key={supplier.id} className={containerClasses} aria-selected={isSelected}>
                          <div>
                            <p className="text-sm font-semibold flex items-center gap-2">
                              {supplier.name}
                              {isSelected && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-600/80 text-white">Selected</span>
                              )}
                            </p>
                            <div className="flex flex-col text-xs text-gray-300">
                              <span className="flex items-center gap-2">
                                <span>
                                  Current: {formatCurrency(currentRate, 3)}/kWh{scheduleText ? '' : ` (fixed)`} ({phase})
                                  {typeof nextChangeH === 'number' && (
                                    <span className="text-gray-400"> • Next change in {nextChangeH}h</span>
                                  )}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeClass}`}>{phase}</span>
                                {isBestNow && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-700/80 text-white">Best now</span>
                                )}
                              </span>
                              <span className="text-gray-400">Base: {formatCurrency(supplier.costPerKWh, 3)}/kWh • Green: {(supplier.greenEnergy * 100).toFixed(0)}% • Reliability: {(supplier.reliability * 100).toFixed(1)}%</span>
                                {scheduleText && (
                                <span className="text-gray-400" title={scheduleText}>Schedule: {scheduleText}</span>
                              )}
                              {scheduleText && (() => {
                                const feedIn = currentLocationData?.batteryStorage?.exportFeedInFraction ?? 0.6;
                                const schedule2 = supplier.tariffSchedule as Array<{ startHour: number; endHour: number; rate: number }> | undefined;
                                if (!schedule2) return null;
                                const feedInText = schedule2.map(w =>
                                  `${w.startHour.toString().padStart(2,'0')}-${w.endHour.toString().padStart(2,'0')}: ${formatCurrency(w.rate * feedIn, 3)}`
                                ).join('  |  ');
                                return <span className="text-green-600" title="Rate at which surplus renewable energy is sold back to the grid">Feed-in: {feedInText}/kWh</span>;
                              })()}
                            </div>
                          </div>
                          <button 
                            className={`px-3 py-1 rounded text-xs ${isSelected ? 'bg-blue-700 cursor-default opacity-80' : 'bg-blue-600 hover:bg-blue-700'}`}
                            onClick={() => !isSelected && currentLocationData.powerGridId && selectPowerSupplier(currentLocation, supplier.id)}
                            disabled={isSelected || !currentLocationData.powerGridId}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-4 space-y-3">
              <p className="text-sm text-gray-400">No power grid connection</p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <span className="text-sm text-gray-300">Grid</span>
                <span className="text-sm font-semibold text-white">
                  {recommendedGridId ? (powerGrids[recommendedGridId]?.locationName ?? recommendedGridId) : 'No grid available'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  className={`py-2 px-4 rounded text-sm font-semibold ${money < 5000 ? 'bg-blue-900 cursor-not-allowed opacity-60' : 'bg-blue-600 hover:bg-blue-700'}`}
                  onClick={() => {
                    const CONNECTION_COST = 5000;
                    if (money < CONNECTION_COST) {
                      triggerNotification(`Not enough money. Need ${formatCurrency(CONNECTION_COST)}`, 'error');
                      return;
                    }

                    const targetGridId = selectedGridId || recommendGridForLocation(currentLocation, powerGrids);
                    if (!targetGridId) {
                      triggerNotification('No available power grids to connect.', 'error');
                      return;
                    }
                    const grid = powerGrids[targetGridId];
                    if (!grid) {
                      triggerNotification('Selected power grid not available', 'error');
                      return;
                    }

                    setLocations(prev => {
                      const loc = prev[currentLocation];
                      if (!loc) return prev;
                      const updated = JSON.parse(JSON.stringify(loc));
                      updated.powerGridId = targetGridId;
                      const grossPower = calculateLocationGrossPower(updated, powerGrids);
                      updated.grossPowerKW = grossPower;
                      updated.availablePowerKW = Math.max(0, grossPower - (updated.usedPower || 0));
                      const next = { ...prev };
                      next[currentLocation] = updated;
                      return next;
                    });

                    setMoney(prev => prev - CONNECTION_COST);
                    triggerNotification(
                      `Connected to ${grid.locationName} for ${formatCurrency(CONNECTION_COST)}. Select a power supplier to receive power.`,
                      'success'
                    );
                  }}
                  disabled={money < 5000}
                >
                  Connect to Power Grid - {formatCurrency(5000)}
                </button>
                {money < 5000 && (
                  <span className="text-xs text-red-400">Insufficient funds. You need {formatCurrency(5000)}.</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Next-step footer — chains the setup flow toward the cooling modal */}
        {onGoToCooling && (
          <div className="mt-6 border-t border-gray-700 pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-sm">
              {powerChainComplete ? (
                <span className="text-emerald-400 font-medium flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4" /> Power chain complete — cooling is next.
                </span>
              ) : (
                <span className="text-gray-400">Finish the power chain above, then set up cooling to keep your racks from overheating.</span>
              )}
            </div>
            <button
              onClick={onGoToCooling}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                powerChainComplete
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              }`}
            >
              <Snowflake className="w-4 h-4" />
              {powerChainComplete ? 'Set up Cooling' : 'Go to Cooling'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PowerManagementModal;
